import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FlaskConical, CheckCircle2, XCircle, Loader2, X, HelpCircle, Trophy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fullImg = (p) => `${process.env.REACT_APP_BACKEND_URL}${p}`;

const ARMS = [
  { id: 'raw', label: 'A · Masque top-down brut', hint: 'mask_projected: false' },
  { id: 'projected', label: 'B · Masque projeté iso 45°', hint: 'mask_projected: true' },
];

const scoreTone = (s) => (s == null ? 'var(--c-text-muted)' : s >= 80 ? '#34D399' : s >= 60 ? '#FBBF24' : '#FCA5A5');

const Tile = ({ it, idx, arm }) => {
  const chk = it.camera_check;
  return (
    <div className="relative aspect-square rounded-lg overflow-hidden flex items-center justify-center" style={{ border: `1px solid ${it.fidelity_score != null ? scoreTone(it.fidelity_score) : 'var(--c-border)'}`, background: 'rgba(0,0,0,0.3)' }} data-testid={`ab-tile-${arm}-${idx}`}>
      {it.image_url ? (
        <a href={fullImg(it.image_url)} target="_blank" rel="noreferrer" className="block w-full h-full">
          <img src={fullImg(it.image_url)} alt={`Rendu ${arm} ${idx + 1}`} className="w-full h-full object-cover" />
        </a>
      ) : it.status === 'failed' ? (
        <span className="text-[10px] px-2 text-center" style={{ color: '#FCA5A5' }}>Échec{it.error ? ` · ${String(it.error).slice(0, 40)}` : ''}</span>
      ) : (
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--c-text-muted)' }} />
      )}
      <span className="absolute top-1 left-1 text-[10px] px-1 rounded hud-tabular" style={{ background: 'rgba(0,0,0,0.6)', color: '#E5E7EB' }}>#{idx + 1}</span>
      {it.status === 'completed' && (
        <>
          <span className="absolute bottom-1 left-1 text-[11px] font-semibold px-1.5 rounded hud-tabular" style={{ background: 'rgba(0,0,0,0.7)', color: scoreTone(it.fidelity_score) }} title={(it.fidelity_issues || []).join(' · ')} data-testid={`ab-score-${arm}-${idx}`}>
            {it.fidelity_score == null ? <Loader2 className="w-3 h-3 animate-spin inline" /> : `Plan ${it.fidelity_score}%`}
          </span>
          <span className="absolute bottom-1 right-1 rounded-full p-0.5" style={{ background: 'rgba(0,0,0,0.65)' }} title={chk?.note || (chk ? 'Cadrage' : 'Analyse du cadrage…')}>
            {!chk ? <Loader2 className="w-3.5 h-3.5 animate-spin text-white" /> : chk.ok ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#34D399' }} /> : chk.ok === false ? <XCircle className="w-3.5 h-3.5" style={{ color: '#FCA5A5' }} /> : <HelpCircle className="w-3.5 h-3.5 text-white" />}
          </span>
        </>
      )}
    </div>
  );
};

const ArmColumn = ({ arm, data, winner }) => {
  const items = data?.items || [];
  const isWinner = winner === arm.id;
  return (
    <div className="rounded-lg p-3" style={{ border: `1px solid ${isWinner ? 'rgba(52,211,153,0.6)' : 'var(--c-border)'}`, background: isWinner ? 'rgba(52,211,153,0.06)' : 'rgba(0,0,0,0.15)' }} data-testid={`ab-arm-${arm.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div>
          <div className="text-[12px] font-semibold flex items-center gap-1.5" style={{ color: 'var(--c-text)' }}>
            {isWinner && <Trophy className="w-3.5 h-3.5" style={{ color: '#34D399' }} />}{arm.label}
          </div>
          <div className="text-[10px] hud-tabular" style={{ color: 'var(--c-text-muted)' }}>{arm.hint}</div>
        </div>
        <div className="text-right">
          <div className="text-[16px] font-bold hud-tabular" style={{ color: scoreTone(data?.avg) }} data-testid={`ab-avg-${arm.id}`}>
            {data?.avg != null ? `${data.avg}%` : '—'}
          </div>
          <div className="text-[10px] hud-tabular" style={{ color: 'var(--c-text-muted)' }}>
            {data?.scored ? `min ${data.min} · max ${data.max} · caméra ${data.camera_ok}/${data.completed}` : `${data?.completed || 0}/${data?.total || items.length} rendus`}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {items.map((it, idx) => <Tile key={it.job_id} it={it} idx={idx} arm={arm.id} />)}
      </div>
    </div>
  );
};

/** ABProjectionGrid — suit un test A/B masque brut vs projeté (maquette blanche) : fidélité + caméra par bras. */
export const ABProjectionGrid = ({ projectId, testId, onClose }) => {
  const [data, setData] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!testId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}/generation/ab-projection-test/${testId}`, { withCredentials: true });
        if (cancelled) return;
        setData(r.data);
        if (!r.data.scoring_done) timer.current = setTimeout(poll, 6000);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Suivi du test A/B indisponible');
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [projectId, testId]);

  if (!testId) return null;
  const v = data?.verdict;
  const verdictText = !data ? '…'
    : !data.scoring_done ? 'Rendus et notation en cours…'
      : !v ? 'Pas assez de rendus notés pour trancher'
        : v.winner === 'tie' ? `Égalité (Δ ${v.delta > 0 ? '+' : ''}${v.delta} pt)`
          : `Gagnant : ${v.winner === 'raw' ? 'masque brut' : 'masque projeté'} (Δ ${v.delta > 0 ? '+' : ''}${v.delta} pt)`;

  return (
    <div className="mt-4 rounded-xl p-4" style={{ border: '1px solid rgba(217,160,91,0.35)', background: 'rgba(217,160,91,0.05)' }} data-testid="ab-projection-grid">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4" style={{ color: 'var(--c-gold)' }} />
          <span className="text-[12px] font-semibold" style={{ color: 'var(--c-text)' }}>A/B projection du masque · maquette blanche</span>
          <span className="text-[11px]" style={{ color: 'var(--c-text-muted)' }}>{data?.vue_angle || 'Aérienne isométrique 45°'}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[12px] font-semibold hud-tabular" style={{ color: v && v.winner !== 'tie' ? '#34D399' : 'var(--c-text-muted)' }} data-testid="ab-verdict">{verdictText}</span>
          <button type="button" onClick={onClose} className="hud-btn hud-btn-ghost hud-btn-sm aspect-square px-0" data-testid="ab-close"><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {ARMS.map((arm) => <ArmColumn key={arm.id} arm={arm} data={data?.arms?.[arm.id]} winner={v?.winner} />)}
      </div>
    </div>
  );
};

export default ABProjectionGrid;
